import { Add } from "@carbon/icons-react";
import IconLoading from "@Components/IconLoading/IconLoading";
import InputTagSelection from "@Components/Input/InputTagSelection";
import InputText, { InputAction } from "@Components/Input/InputText/InputText";
import { DEBOUNCE_TIME_300 } from "@Configs/consts";
import { BORDER_TYPE } from "@Configs/enum";
import { CommonService } from "@Services/common-service";
import { useDebounceFn } from "ahooks";
import { Checkbox, Empty, Tooltip } from "antd";
import { SearchIcon } from "assets/icons";
import classNames from "classnames";
import React, { RefObject } from "react";
import { Model, ModelFilter } from "react-3layer-common";
import type { ErrorObserver, Observable } from "rxjs";
import "./MultipleSelect.scss";
import EmptyCM from "@Components/EmptyCM";

export interface MultipleSelectProps<
  T extends Model,
  TFilter extends ModelFilter
> {
  /**list value users select*/
  values?: Model[];
  /**Value filter for api get data option*/
  valueFilter?: TFilter;
  /**The property name of the model filter you want to search in the list data*/
  searchProperty?: string;
  /**The type of searchProperty you want to search in the list data*/
  searchType?: string;
  /**Placeholder of the component*/
  placeHolder?: string;
  /**Not allow to handle change value*/
  disabled?: boolean;
  /**Show symbol * as required field*/
  isRequired?: boolean;
  /**Append this component to body*/
  appendToBody?: boolean;
  /**Api to get list data*/
  getList?: (TModelFilter?: TFilter) => Observable<T[]>;
  /**Handle the change value of the field*/
  onChange?: (selectedList?: T[], ids?: []) => void;
  /**Provide a function to render a specific property as name*/
  render?: (t: T) => string;
  /**Model filter class of API get list data*/
  classFilter: new () => TFilter;
  /**Label for current field*/
  label?: string;
  /**Control the style type of component: MATERIAL, BORDERED, FLOAT_LABEL */
  type?: BORDER_TYPE;
  /**Control the size of the component*/
  isSmall?: boolean;
  /**Option show button add new*/
  selectWithAdd?: () => void;
  /**Component enable to search data list*/
  isUsingSearch?: boolean;
  /**Not expand the title row to see more detail on each post (each row)*/
  isNotExpand?: boolean;
  /**Set enum value */
  isEnumerable?: boolean;
  /**Set material */
  isMaterial?: boolean;
  /**Prefer option to select*/
  preferOptions?: T[];
  /**Set show tooltip or not */
  isShowTooltip?: boolean;
  /** Provide a custom action (onClick) to the component */
  action?: InputAction;
  /** Custom background color for component: "white" || "gray" */
  bgColor?: "white" | "gray";
  /**Use to custom style the component*/
  className?: string;
  /**Handle onKeyDown action*/
  onKeyDown?: (event: any) => void;
  /**Handle onEnter action*/
  onKeyEnter?: (event: any) => void;
}

function defaultRenderObject<T extends Model>(t: T) {
  return t?.name;
}

function MultipleSelect(props: MultipleSelectProps<Model, ModelFilter>) {
  const {
    values,
    valueFilter,
    searchProperty = "name",
    searchType = "contain",
    placeHolder,
    disabled = false,
    // isEnumerable = false,
    // isMaterial = false,
    isNotExpand,
    onKeyDown,
    onKeyEnter,
    isRequired,
    appendToBody,
    getList,
    onChange,
    render = defaultRenderObject,
    classFilter: ClassFilter,
    label,
    type,
    isSmall = true,
    selectWithAdd,
    isUsingSearch = true,
    // preferOptions,
    isShowTooltip = true,
    action,
    bgColor,
    className,
  } = props;

  const [loading, setLoading] = React.useState<boolean>(false);

  const [list, setList] = React.useState<Model[]>([]);

  const [autoFocusInput, setAutoFocusInput] = React.useState<boolean>(false);

  const [isExpand, setExpand] = React.useState<boolean>(false);

  const wrapperRef: RefObject<HTMLDivElement> =
    React.useRef<HTMLDivElement>(null);

  const [reCaculateWidth, setReCaculateWidth] = React.useState<boolean>(false);

  const selectListRef: RefObject<HTMLDivElement> =
    React.useRef<HTMLDivElement>(null);

  const [appendToBodyStyle, setAppendToBodyStyle] = React.useState({});

  const [subscription] = CommonService.useSubscription();

  const [searchTerm, setSearchTerm] = React.useState<string>("");

  const handleClearItem = React.useCallback(
    (item: Model) => {
      if (!disabled) {
        const newValues = values?.filter((p) => p?.id !== item?.id);
        const ids = newValues?.map((item) => item?.id);
        onChange([...(newValues ? newValues : [])], [
          ...(ids ? ids : []),
        ] as any);
      }
    },
    [disabled, values]
  );

  const handleKeyDown = React.useCallback(
    (event: any) => {
      if (typeof onKeyDown === "function") {
        onKeyDown(event);
      }
    },
    [onKeyDown]
  );

  const handleEnter = React.useCallback(
    (event: any) => {
      if (typeof onKeyEnter === "function") {
        onKeyEnter(event);
      }
    },
    [onKeyEnter]
  );

  const handleGetList = React.useCallback(
    async (filterValue: ModelFilter) => {
      subscription.add(getList);
      setLoading(true);
      getList(filterValue).subscribe({
        next: (res: Model[]) => {
          setList(res);
          setLoading(false);
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        error: (err: ErrorObserver<Error>) => {
          setList([]);
          setLoading(false);
        },
      });
    },
    [getList, subscription]
  );

  const { run } = useDebounceFn(
    (searchTerm: string) => {
      const cloneValueFilter = valueFilter
        ? JSON.parse(JSON.stringify(valueFilter))
        : new ClassFilter();
      if (searchType) {
        cloneValueFilter[searchProperty][searchType] = searchTerm;
      } else {
        cloneValueFilter[searchProperty] = searchTerm;
      }
      handleGetList(cloneValueFilter);
    },
    {
      wait: DEBOUNCE_TIME_300,
    }
  );
  const internalList = React.useMemo(() => {
    if (list && list.length > 0) {
      list.forEach((current) => {
        const filteredItem =
          values &&
          values.length > 0 &&
          values?.filter((item) => item.id === current.id)[0];
        if (filteredItem) {
          current.isSelected = true;
        } else {
          current.isSelected = false;
        }
      });
      return [...list];
    }
    return [];
  }, [list, values]);

  // const internalPreferOptions = React.useMemo(() => {
  //   if (preferOptions && preferOptions.length > 0) {
  //     preferOptions.forEach((current) => {
  //       // eslint-disable-next-line prefer-const
  //       let filteredItem =
  //         values &&
  //         values?.length > 0 &&
  //         values.filter((item) => item.id === current.id)[0];
  //       if (filteredItem) {
  //         current.isSelected = true;
  //       } else {
  //         current.isSelected = false;
  //       }
  //     });
  //     return [...preferOptions];
  //   }
  //   return [];
  // }, [preferOptions, values]);

  const handleLoadList = React.useCallback(() => {
    try {
      const filter = valueFilter ? valueFilter : new ClassFilter();
      handleGetList(filter);
    } catch (error) {
      //
    }
  }, [valueFilter, ClassFilter, handleGetList]);

  const handleToggle = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!disabled && !isExpand) {
        setExpand(true);
        await handleLoadList();
        setAutoFocusInput(true);
      }
    },
    [disabled, isExpand, handleLoadList]
  );

  const handleCloseSelect = React.useCallback(() => {
    setExpand(false);
    setSearchTerm("");
    setAutoFocusInput(false);
  }, []);

  const handleClickItem = React.useCallback(
    (item: Model) => (event: any) => {
      const filteredItem = values?.filter(
        (current) => current.id === item.id
      )[0];
      if (filteredItem) {
        const tmp = [...(values ? values : [])];
        const ids = values?.map((item) => item?.id);
        const index = tmp.indexOf(filteredItem);
        tmp.splice(index, 1);
        ids.splice(index, 1);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange([...tmp], ids as any);
      } else {
        const ids = values?.map((item) => item?.id);
        onChange([...(values ? values : []), item], [
          ...(ids ? ids : []),
          item?.id,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ] as any);
      }
    },
    [values, onChange]
  );

  const handleClickParentItem = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (event && event.target === event.currentTarget) {
        const currentItem = event.target as HTMLDivElement;
        currentItem.firstElementChild.firstElementChild
          .querySelector("span")
          .click();
      }
    },
    []
  );

  const handleSearchChange = React.useCallback(
    (searchTerm: string) => {
      setSearchTerm(searchTerm);
      run(searchTerm);
    },
    [run]
  );

  const handleClearAll = React.useCallback(() => {
    onChange([], []);
  }, [onChange]);

  const handleKeyPress = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      switch (event.keyCode) {
        case 40:
          if (selectListRef.current) {
            const firstItem = selectListRef.current
              .firstElementChild as HTMLElement;
            firstItem.focus();
          }
          break;
        case 9:
          handleCloseSelect();
          break;
        default:
          return;
      }
    },
    [handleCloseSelect]
  );

  const handleMove = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item: any) => (event: any) => {
      switch (event.keyCode) {
        case 13:
          handleClickItem(item)(null);
          break;
        case 40:
          if (event.target.nextElementSibling !== null) {
            event.target.nextElementSibling.focus();
          }
          event.preventDefault();
          break;
        case 38:
          if (event.target.previousElementSibling !== null) {
            event.target.previousElementSibling.focus();
          }
          event.preventDefault();
          break;
      }
      return;
    },
    [handleClickItem]
  );

  const handleKeyEnter = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      if (event.key === "Enter") {
        handleToggle(null);
      }
      return;
    },
    [handleToggle]
  );

  const handlePressExpandedIcon = () => {
    setExpand(!isExpand);
  };

  React.useEffect(() => {
    if (isExpand) {
      window.addEventListener("resize", () => {
        setReCaculateWidth(true);
      });
    }
    return () =>
      window.removeEventListener("resize", () => {
        setReCaculateWidth(false);
      });
  }, [isExpand]);

  React.useEffect(() => {
    if (appendToBody || reCaculateWidth)
      setTimeout(() => {
        const currentPosition = wrapperRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - currentPosition.bottom;
        if (spaceBelow <= 250) {
          setTimeout(() => {
            setAppendToBodyStyle({
              position: "fixed",
              bottom: spaceBelow + wrapperRef.current.clientHeight,
              left: currentPosition.left,
              maxWidth: wrapperRef.current.clientWidth,
            });
          }, 100);
        } else {
          setAppendToBodyStyle({
            position: "fixed",
            top: currentPosition.top + wrapperRef.current.clientHeight,
            left: currentPosition.left,
            maxWidth: wrapperRef.current.clientWidth,
          });
        }
        setReCaculateWidth(false);
      }, 1000);
  }, [appendToBody, reCaculateWidth]);

  CommonService.useClickOutside(wrapperRef, handleCloseSelect);

  return (
    <>
      <div
        className={classNames(
          "select__container multiple-select__container",
          className
        )}
        ref={wrapperRef}
      >
        <div onClick={handleToggle}>
          <InputTagSelection
            listValue={values}
            render={render}
            placeHolder={placeHolder}
            disabled={disabled}
            onSearch={handleSearchChange}
            label={label}
            onClearMulti={handleClearAll}
            onClear={handleClearItem}
            type={type}
            isSmall={isSmall}
            isUsingSearch={false}
            isNotExpand={!isExpand}
            isRequired={isRequired}
            isShowTooltip={isShowTooltip}
            action={action}
            bgColor={bgColor}
            handlePressExpandedIcon={handlePressExpandedIcon}
            isUsingInSelect={true}
            handleClickLabel={handleCloseSelect}
          />
        </div>

        {isExpand && (
          <div className="select__list-container" style={appendToBodyStyle}>
            {isUsingSearch && (
              <div style={{ padding: 4, paddingBottom: 8 }}>
                <InputText
                  isSmall={isSmall}
                  placeHolder="Tìm kiếm"
                  onChange={handleSearchChange}
                  prefix={<SearchIcon />}
                  disabled={disabled}
                  readOnly={disabled}
                  onKeyDown={isNotExpand ? handleEnter : handleKeyDown}
                  autoFocusInput={autoFocusInput}
                  value={searchTerm}
                />
              </div>
            )}

            {!loading ? (
              <div className="multiple-select__list" ref={selectListRef}>
                {internalList && internalList.length > 0 ? (
                  internalList.map((item, index) => (
                    <div
                      className={classNames(
                        "select__itemMulti p-l--xs p-r--2xs",
                        {
                          "select__itemMulti--selected": item.isSelected,
                          "select__itemMulti--disabled": disabled,
                        }
                      )}
                      key={index}
                      onKeyDown={handleMove(item)}
                      tabIndex={-1}
                      onClick={handleClickParentItem}
                    >
                      <Checkbox
                        checked={item.isSelected}
                        onChange={handleClickItem(item)}
                        disabled={disabled}
                      >
                        <Tooltip title={render(item)} placement="topLeft">
                          <div className="select__text">{render(item)}</div>
                        </Tooltip>
                      </Checkbox>
                    </div>
                  ))
                ) : (
                  <EmptyCM />
                )}
              </div>
            ) : (
              <div className="select__loading">
                <IconLoading color="#0F62FE" size={24} />
              </div>
            )}
            {/* {!loading && list.length > 0 && (
              <div className="select__list-prefer">
                {internalPreferOptions &&
                  internalPreferOptions?.length > 0 &&
                  internalPreferOptions.map((item, index) => (
                    <div
                      className={classNames(
                        "select__prefer-option select__item p-l--xs p-y--xs p-r--2xs"
                      )}
                      key={index}
                      onKeyDown={handleMove(item)}
                      onClick={handleClickParentItem}
                    >
                      <Checkbox
                        onChange={handleClickItem(item)}
                        checked={item.isSelected}
                      >
                        <span className="select__text">
                          {maxLengthItem &&
                          render(item)?.length > maxLengthItem ? (
                            <Tooltip title={item?.name}>
                              {CommonService.limitWord(
                                render(item),
                                maxLengthItem
                              )}
                            </Tooltip>
                          ) : (
                            render(item)
                          )}
                        </span>
                      </Checkbox>
                    </div>
                  ))}
              </div>
            )} */}
            {typeof selectWithAdd !== "undefined" && (
              <div
                className={classNames(
                  "select__bottom-button select__add-button p-y--xs"
                )}
                onClick={selectWithAdd}
              >
                <Add size={16} className="m-l--xs" />
                <span className="m-l--xs">Add new</span>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default MultipleSelect;
